import { useRouteContext } from "@tanstack/react-router";

import { useEnvironments } from "../state/environments";
import { clerkProviderEnabled } from "../cloud/clerkRuntime";
import { isHostedStaticApp } from "../hostedPairing";
import {
  resolveAddProjectRoute,
  shouldMountHostedSurfaces,
  type AddProjectRoute,
} from "./hostedMode";

/**
 * Reads hosted mode from the root route context, which is where the app already
 * decides between a local T3 backend and the hosted Worker. Components outside a
 * route file (the command palette, the sidebar) can't use `Route.useRouteContext`,
 * so this reads it loosely and falls back to the same origin check the root
 * route's `beforeLoad` performs.
 */
export function useHostedAuthGateStatus(): string {
  const contextStatus = useRouteContext({
    strict: false,
    select: (context) =>
      (context as { readonly authGateState?: { readonly status?: string } } | undefined)
        ?.authGateState?.status ?? null,
  });
  if (contextStatus) {
    return contextStatus;
  }
  return isHostedStaticApp() ? "hosted-static" : "unknown";
}

export interface HostedModeState {
  readonly authGateStatus: string;
  readonly environmentCount: number;
  /**
   * Hosted, environmentless, and behind a mounted Clerk provider. This is the
   * only mount condition hosted components should use: every one of them calls
   * a Clerk hook, so hosted-ness alone is not enough.
   */
  readonly hostedSurfacesEnabled: boolean;
  readonly addProjectRoute: AddProjectRoute;
}

export function useHostedModeState(): HostedModeState {
  const authGateStatus = useHostedAuthGateStatus();
  const { environments } = useEnvironments();
  // Build-time configuration, so this is stable for the life of the page and
  // safe to read during render.
  const input = {
    authGateStatus,
    environmentCount: environments.length,
    clerkProviderMounted: clerkProviderEnabled(),
  };

  return {
    authGateStatus,
    environmentCount: environments.length,
    hostedSurfacesEnabled: shouldMountHostedSurfaces(input),
    addProjectRoute: resolveAddProjectRoute(input),
  };
}
